/**
 * Point anchored placement with flipping and shifting.
 *
 * This module is a pure function of numbers. It never reads the DOM, never
 * calls `getBoundingClientRect` and never references `window`: the caller
 * measures, this decides. That is what makes the most bug prone piece of the
 * library testable without a browser.
 *
 * Direction vocabulary follows the plan: "inline-end" is the physical right
 * edge of the viewport and "inline-start" is the physical left edge. What
 * changes with `rtl` is the preferred side, not the meaning of the words.
 */

import type { Rect, Viewport } from './types'

export type { Rect, Viewport } from './types'

export interface PlacementInput {
  anchor: { x: number; y: number }
  size: { width: number; height: number }
  viewport: Viewport
  offset: { x: number; y: number }
  /** For submenus: the parent item's rect, so the menu flanks it instead of covering it. */
  avoid?: Rect
  rtl?: boolean
}

export interface PlacementResult {
  x: number
  y: number
  /** True when the menu did not end up on the preferred side for its direction. */
  flippedX: boolean
  flippedY: boolean
  /** True when the menu had to be clamped back inside the viewport. */
  shifted: boolean
}

/** One axis of a decision: where the menu goes and how it got there. */
interface AxisResult {
  value: number
  flipped: boolean
  shifted: boolean
}

/**
 * Pick between the preferred position and its opposite.
 *
 * The preferred side wins whenever it fits. The opposite side is taken only
 * when it fits and the preferred one does not, so a flip is never a downgrade.
 * When neither fits the side with more room wins, which keeps the later clamp
 * from hiding most of the menu.
 */
function choose(
  preferred: number,
  opposite: number,
  preferredFits: boolean,
  oppositeFits: boolean,
  preferredRoom: number,
  oppositeRoom: number,
): { value: number; flipped: boolean } {
  if (preferredFits) return { value: preferred, flipped: false }
  if (oppositeFits) return { value: opposite, flipped: true }

  return oppositeRoom > preferredRoom
    ? { value: opposite, flipped: true }
    : { value: preferred, flipped: false }
}

/** Clamp into `[lo, hi]`, never below zero, and report whether it moved. */
function clamp(value: number, lo: number, hi: number): { value: number; shifted: boolean } {
  const low = Math.max(0, lo)
  const high = Math.max(low, hi)
  const clamped = Math.min(Math.max(value, low), high)

  return { value: clamped, shifted: clamped !== value }
}

/** Rule 2: below the anchor by default, above it when the bottom overflows. */
function placeVertical(
  anchorY: number,
  height: number,
  viewportHeight: number,
  offsetY: number,
): AxisResult {
  const below = anchorY + offsetY
  const above = anchorY - height - offsetY

  const chosen = choose(
    below,
    above,
    below + height <= viewportHeight,
    above >= 0,
    viewportHeight - anchorY,
    anchorY,
  )

  const clamped = clamp(chosen.value, 0, viewportHeight - height)

  return { value: clamped.value, flipped: chosen.flipped, shifted: clamped.shifted }
}

/**
 * Rules 1, 3 and 6: the menu grows towards the inline-end in LTR and towards
 * the inline-start in RTL, and flips to the other side when its preferred one
 * overflows.
 */
function placeHorizontal(
  anchorX: number,
  width: number,
  viewportWidth: number,
  offsetX: number,
  rtl: boolean,
): AxisResult {
  const towardsEnd = anchorX + offsetX
  const towardsStart = anchorX - width - offsetX

  const endFits = towardsEnd + width <= viewportWidth
  const startFits = towardsStart >= 0
  const endRoom = viewportWidth - anchorX
  const startRoom = anchorX

  const chosen = rtl
    ? choose(towardsStart, towardsEnd, startFits, endFits, startRoom, endRoom)
    : choose(towardsEnd, towardsStart, endFits, startFits, endRoom, startRoom)

  const clamped = clamp(chosen.value, 0, viewportWidth - width)

  return { value: clamped.value, flipped: chosen.flipped, shifted: clamped.shifted }
}

/**
 * Rule 4: a submenu flanks its parent item's rect instead of being placed at a
 * point. Non overlap is stronger than the viewport clamp here, because a
 * submenu drawn on top of the item that opened it is unusable, while one that
 * pokes a few pixels off screen is merely untidy.
 */
function placeFlanking(
  avoid: Rect,
  width: number,
  viewportWidth: number,
  offsetX: number,
  rtl: boolean,
): AxisResult {
  const avoidEnd = avoid.x + avoid.width
  const towardsEnd = avoidEnd + offsetX
  const towardsStart = avoid.x - width - offsetX

  const endFits = towardsEnd >= 0 && towardsEnd + width <= viewportWidth
  const startFits = towardsStart >= 0 && towardsStart + width <= viewportWidth
  const endRoom = viewportWidth - avoidEnd
  const startRoom = avoid.x

  const chosen = rtl
    ? choose(towardsStart, towardsEnd, startFits, endFits, startRoom, endRoom)
    : choose(towardsEnd, towardsStart, endFits, startFits, endRoom, startRoom)

  const onStartSide = chosen.value + width <= avoid.x
  const clamped = onStartSide
    ? clamp(chosen.value, 0, Math.min(avoid.x - width, viewportWidth - width))
    : clamp(chosen.value, avoidEnd, viewportWidth - width)

  return { value: clamped.value, flipped: chosen.flipped, shifted: clamped.shifted }
}

/**
 * Decide where a menu of `size` goes for a trigger at `anchor`.
 *
 * With `avoid` given the horizontal axis flanks that rect and the vertical axis
 * still follows the anchor, so a submenu lines up with the item that opened it.
 */
export function place(input: PlacementInput): PlacementResult {
  const { anchor, size, viewport, offset, avoid, rtl = false } = input

  const vertical = placeVertical(anchor.y, size.height, viewport.height, offset.y)
  const horizontal = avoid
    ? placeFlanking(avoid, size.width, viewport.width, offset.x, rtl)
    : placeHorizontal(anchor.x, size.width, viewport.width, offset.x, rtl)

  return {
    x: horizontal.value,
    y: vertical.value,
    flippedX: horizontal.flipped,
    flippedY: vertical.flipped,
    shifted: horizontal.shifted || vertical.shifted,
  }
}
